/**
 * Адміністрування акаунта → вкладка «Ролі» (Порція Б.5; ч.1089).
 *
 * Показує готові (системні, незмінні) і власні ролі поточного акаунта.
 * (Системних ролей у БД 16; основою для копіювання дозволені лише 11 —
 * див. CANONICAL_SYSTEM_ROLE_CODES.)
 * Дії: створити, редагувати, деактивувати/реактивувати; ч.1089 — «N прав»
 * відкриває попап з переліком прав ролі людськими назвами.
 *
 * Захист доступу — `<RequirePermission code="admin.access.manage">`
 * (дублювання backend-enforce для UX; backend все одно поверне 403).
 *
 * Джерело істини — backend:
 *  - 7 заборонених permissions НЕ показуються у формі (вони все одно
 *    дадуть 422 від _validate_permissions_allowed)
 *  - Variant A: основа — лише канонічні 11 дозволених системних ролей-основ
 *  - права-резерви (`enforced=false` у довіднику прав) — сірим, «поки не діє»
 *
 * Деактивація — М'ЯКА: роль перестає давати права носіям, але її можна
 * реактивувати; нічого не видаляється.
 *
 * ч.1089 (перевірка власника): технічні коди ролей і прав клієнту не
 * показуємо — лише людські назви; коди — у підказках при наведенні.
 */
import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  accessRoles,
  CANONICAL_SYSTEM_ROLE_CODES,
  FORBIDDEN_IN_CUSTOM_ROLES,
  type AccessRole,
} from '@/api/admin';
import { useConfirm } from '@/components/ConfirmProvider';
import { humanizeBackendError } from '@/utils/backendError';
import RoleEditorDialog from './RoleEditorDialog';
import RolePermissionsDialog from './RolePermissionsDialog';
import DocStatusBadge from '@/shared/components/DocStatusBadge';

export default function RolesSection() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [editorRole, setEditorRole] = useState<AccessRole | 'CREATE' | null>(null);
  const [permsRole, setPermsRole] = useState<AccessRole | null>(null);

  const { data: roles, isLoading } = useQuery({
    queryKey: ['access-roles'],
    queryFn: () => accessRoles.list(),
  });

  const { system, customActive, customDeactivated } = useMemo(() => {
    const all = roles ?? [];
    return {
      system: all.filter((r) => r.is_system).sort((a, b) =>
        (a.sort_order ?? 100) - (b.sort_order ?? 100)
      ),
      customActive: all.filter((r) => !r.is_system && r.status === 'ACTIVE')
        .sort((a, b) => a.name.localeCompare(b.name, 'uk')),
      customDeactivated: all.filter((r) => !r.is_system && r.status === 'DEACTIVATED')
        .sort((a, b) => a.name.localeCompare(b.name, 'uk')),
    };
  }, [roles]);
  const systemNameByCode = useMemo(
    () => new Map(system.map((r) => [r.code, r.name])), [system]);

  const deactivateMut = useMutation({
    mutationFn: (id: string) => accessRoles.deactivate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['access-roles'] }),
    onError: (e) => alert(humanizeBackendError(e)),
  });

  const reactivateMut = useMutation({
    mutationFn: (id: string) => accessRoles.reactivate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['access-roles'] }),
    onError: (e) => alert(humanizeBackendError(e)),
  });

  async function onDeactivate(role: AccessRole) {
    // Беремо assignees щоб показати N у діалозі
    let assigneesCount = 0;
    try {
      const ass = await accessRoles.assignees(role.id);
      assigneesCount = ass.length;
    } catch {
      // непринципово — показуємо діалог без точного числа
    }
    const msg = assigneesCount > 0
      ? `Роль «${role.name}» призначено ${assigneesCount} користувачам. ` +
        `Після деактивації вони втратять повʼязані права при наступній дії в системі. ` +
        `Поточна робота не переривається. Роль можна реактивувати пізніше. Продовжити?`
      : `Деактивувати роль «${role.name}»? Її можна буде реактивувати.`;
    const ok = await confirm({
      message: msg,
      okLabel: 'Деактивувати',
      cancelLabel: 'Скасувати',
    });
    if (ok) deactivateMut.mutate(role.id);
  }

  if (isLoading) return <div>Завантаження ролей…</div>;

  const tableProps = { systemNameByCode, onShowPermissions: setPermsRole };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '1rem' }}>
        <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
          Готові ролі змінювати не можна. Власну роль створюють копією готової і
          вмикають у ній потрібні права. Натисніть на кількість прав, щоб побачити
          їх перелік.
        </div>
        <button type="button" className="primary" onClick={() => setEditorRole('CREATE')}>
          створити власну роль
        </button>
      </div>

      <h4 style={{ marginTop: '1.5rem' }}>Готові ролі ({system.length})</h4>
      <RolesTable roles={system} readOnly {...tableProps} />

      <h4 style={{ marginTop: '1.5rem' }}>Власні ролі ({customActive.length})</h4>
      {customActive.length === 0 ? (
        <div style={{ color: 'var(--text-secondary)', padding: '1rem' }}>
          Власних ролей ще немає. Натисніть «створити власну роль».
        </div>
      ) : (
        <RolesTable
          roles={customActive}
          onEdit={(r) => setEditorRole(r)}
          onDeactivate={onDeactivate}
          {...tableProps}
        />
      )}

      {customDeactivated.length > 0 && (
        <details style={{ marginTop: '1.5rem' }}>
          <summary style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}>
            Деактивовані ({customDeactivated.length})
          </summary>
          <div style={{ marginTop: '0.5rem', opacity: 0.6 }}>
            <RolesTable
              roles={customDeactivated}
              onReactivate={(r) => reactivateMut.mutate(r.id)}
              {...tableProps}
            />
          </div>
        </details>
      )}

      {editorRole && (
        <RoleEditorDialog
          role={editorRole === 'CREATE' ? null : editorRole}
          systemRoles={system}
          onClose={() => setEditorRole(null)}
          onSaved={() => {
            setEditorRole(null);
            queryClient.invalidateQueries({ queryKey: ['access-roles'] });
          }}
        />
      )}

      {permsRole && (
        <RolePermissionsDialog role={permsRole} onClose={() => setPermsRole(null)} />
      )}
    </div>
  );
}

// ============== Таблиця ролей =====================================

function RolesTable({
  roles,
  readOnly = false,
  systemNameByCode,
  onShowPermissions,
  onEdit,
  onDeactivate,
  onReactivate,
}: {
  roles: AccessRole[];
  readOnly?: boolean;
  systemNameByCode: Map<string, string>;
  onShowPermissions: (r: AccessRole) => void;
  onEdit?: (r: AccessRole) => void;
  onDeactivate?: (r: AccessRole) => void;
  onReactivate?: (r: AccessRole) => void;
}) {
  const td = { padding: '0.5rem', verticalAlign: 'top' } as const;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
          <th style={td}>Назва</th>
          {!readOnly && <th style={td}>На основі</th>}
          <th style={td}>Прав</th>
          {!readOnly && <th style={td}>Дії</th>}
        </tr>
      </thead>
      <tbody>
        {roles.map((r) => (
          <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
            <td style={{ ...td, fontWeight: r.is_system ? 600 : 400 }} title={r.code}>
              {r.name}
              {/* ч.1119: роль лише для перегляду — змінювати дані з нею не можна. */}
              {r.is_read_only && (
                <span style={{ marginLeft: '0.5rem', fontWeight: 400 }}
                  title="Користувач із цією роллю бачить дані, але не може створювати, змінювати чи проводити документи">
                  <DocStatusBadge label="лише перегляд" tone="amber" />
                </span>
              )}
              {/* ч.1312: роль звужує окремі види записів «лише свої». */}
              {Object.keys(r.record_scopes ?? {}).length > 0 && (
                <span style={{ marginLeft: '0.5rem', fontWeight: 400 }}
                  title="Для частини записів користувач бачить лише свої — за які він відповідальний (і, де вказано, зроблені на їх підставі). Деталі — у переліку прав ролі">
                  <DocStatusBadge label="лише свої записи" tone="blue" />
                </span>
              )}
              {r.status === 'DEACTIVATED' && (
                <span style={{ marginLeft: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.75rem', fontWeight: 400 }}>
                  (деактивовано)
                </span>
              )}
              {r.description && (
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: 400, marginTop: '0.15rem' }}>
                  {r.description}
                </div>
              )}
            </td>
            {!readOnly && (
              <td style={{ ...td, color: 'var(--text-secondary)' }}>
                {r.created_from_role_code
                  ? (systemNameByCode.get(r.created_from_role_code) ?? r.created_from_role_code)
                  : '—'}
              </td>
            )}
            <td style={td}>
              <button type="button" className="btn-link"
                title="Показати перелік прав"
                onClick={() => onShowPermissions(r)}>
                {r.permissions.length} прав
              </button>
            </td>
            {!readOnly && (
              <td style={{ ...td, whiteSpace: 'nowrap' }}>
                {r.status === 'ACTIVE' && onEdit && (
                  <button type="button" onClick={() => onEdit(r)} style={{ marginRight: '0.5rem' }}>
                    редагувати
                  </button>
                )}
                {r.status === 'ACTIVE' && onDeactivate && (
                  <button type="button" onClick={() => onDeactivate(r)}>
                    деактивувати
                  </button>
                )}
                {r.status === 'DEACTIVATED' && onReactivate && (
                  <button type="button" onClick={() => onReactivate(r)}>
                    реактивувати
                  </button>
                )}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Експорт констант — для тестів grep-guard.
export { CANONICAL_SYSTEM_ROLE_CODES, FORBIDDEN_IN_CUSTOM_ROLES };
